"use client";

import * as stylex from "@stylexjs/stylex";
import { type ReactNode, useEffect, useRef } from "react";

import { colors, radii } from "@softmax/design-system/tokens.stylex";

function GameLoop({
  src,
  poster,
}: {
  readonly src: string;
  readonly poster: string | undefined;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let visible = false;
    function update() {
      if (!video) return;

      if (!visible || motion.matches) {
        video.pause();
        return;
      }

      video.muted = true;
      // Autoplay restrictions leave the poster visible until playback is allowed.
      void video.play().catch(() => video.pause());
    }
    const observer = new IntersectionObserver(
      (entries) => {
        visible = entries.some((entry) => entry.isIntersecting);
        update();
      },
      { threshold: 0.15 },
    );
    observer.observe(video);
    motion.addEventListener("change", update);
    return () => {
      observer.disconnect();
      motion.removeEventListener("change", update);
      video.pause();
    };
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      aria-hidden="true"
      muted
      loop
      playsInline
      preload="metadata"
      {...stylex.props(styles.catalogMedia)}
    />
  );
}

export function CatalogThumbnail({
  board,
  loop,
  thumb,
}: {
  readonly board?: ReactNode;
  readonly loop?: string | null;
  readonly thumb?: string | null;
}) {
  return (
    <span {...stylex.props(styles.catalog)}>
      {board ? (
        <span {...stylex.props(styles.catalogBoard)}>
          <span
            data-generals-plate="window"
            {...stylex.props(styles.boardWindow)}
          >
            <span {...stylex.props(styles.nativeBoard)}>{board}</span>
          </span>
        </span>
      ) : loop ? (
        <GameLoop key={loop} src={loop} poster={thumb ?? undefined} />
      ) : thumb ? (
        <img
          src={thumb}
          alt=""
          loading="lazy"
          {...stylex.props(styles.catalogMedia)}
        />
      ) : null}
    </span>
  );
}
const styles = stylex.create({
  catalog: {
    position: "relative",
    display: "block",
    aspectRatio: "322 / 181",
    width: "100%",
    overflow: "hidden",
    borderRadius: radii.medium,
    backgroundColor: colors.surfaceAlt,
  },
  catalogMedia: {
    position: "absolute",
    inset: 0,
    display: "block",
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  catalogBoard: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#e6e6e6",
  },
  boardWindow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "clip",
    width: "round(down, min(100%, 468px), 52px)",
    height: "round(down, 100%, 52px)",
  },
  nativeBoard: { flexShrink: 0 },
});
